import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Eye, Trash2, UserSearch, Info } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const NIVEL_STYLES = {
    alto: "bg-emerald-100 text-emerald-800",
    medio: "bg-amber-100 text-amber-800",
    bajo: "bg-gray-100 text-gray-600",
};
const NIVEL_LABELS = { alto: "Alto ajuste", medio: "Ajuste medio", bajo: "Bajo ajuste" };

export default function ContratacionDetalle() {
    const { postingId } = useParams();
    const navigate = useNavigate();

    const [posting, setPosting] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [{ data: p }, { data: c }] = await Promise.all([
                api.get(`/hiring/postings/${postingId}`),
                api.get(`/hiring/postings/${postingId}/candidates`),
            ]);
            setPosting(p);
            setCandidates(c || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando la vacante");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [postingId]);

    async function toggleEstado() {
        const nuevoEstado = posting.estado === "abierta" ? "cerrada" : "abierta";
        try {
            await api.patch(`/hiring/postings/${postingId}`, { estado: nuevoEstado });
            toast.success(nuevoEstado === "cerrada" ? "Vacante cerrada" : "Vacante reabierta");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error actualizando la vacante");
        }
    }

    async function uploadCandidate(file) {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("nombre", file.name.replace(/\.pdf$/i, ""));
            await api.post(`/hiring/postings/${postingId}/candidates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("CV evaluado y agregado");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo el CV");
        } finally {
            setUploading(false);
        }
    }

    function handleFiles(fileList) {
        Array.from(fileList || []).forEach((f) => uploadCandidate(f));
    }

    async function removeCandidate(candidateId) {
        try {
            await api.delete(`/hiring/postings/${postingId}/candidates/${candidateId}`);
            toast.success("Candidato eliminado");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error eliminando candidato");
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!posting) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-3xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-6">
                <div className="flex items-start justify-between gap-4 mb-2">
                    <h1 className="text-xl font-semibold inline-flex items-center gap-2"><UserSearch size={20} className="text-edu-navy" /> {posting.titulo}</h1>
                    <button onClick={toggleEstado} className={`text-xs px-3 py-1.5 rounded-lg border ${posting.estado === "abierta" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-edu text-edu-muted"}`}>
                        {posting.estado === "abierta" ? "Abierta" : "Cerrada"} — click para cambiar
                    </button>
                </div>
                <p className="text-sm text-edu-body whitespace-pre-wrap">{posting.descripcion}</p>
            </div>

            <div className="edu-card p-4 mb-4 bg-edu-soft/50 flex items-start gap-2 text-xs text-edu-muted">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                La IA solo ordena y comenta los CV según el puesto descrito — no descarta ni aprueba a nadie automáticamente. Todos los candidatos quedan visibles; la decisión final es tuya.
            </div>

            <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest">Candidatos ({candidates.length})</h2>
                <label className="edu-btn-primary px-3 py-2 text-xs inline-flex items-center gap-1.5 cursor-pointer disabled:opacity-50">
                    <Upload size={13} /> {uploading ? "Subiendo…" : "Subir CV (PDF)"}
                    <input type="file" accept=".pdf" multiple disabled={uploading} className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
                </label>
            </div>

            {candidates.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">Aún no se han subido CV para esta vacante.</div>
            ) : (
                <div className="space-y-3">
                    {candidates.map((c) => {
                        const ev = c.evaluacion || {};
                        return (
                            <div key={c.id} className="edu-card p-5">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div>
                                        <p className="font-medium text-sm">{c.nombre}</p>
                                        <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${NIVEL_STYLES[ev.nivel_ajuste] || NIVEL_STYLES.medio}`}>
                                            {NIVEL_LABELS[ev.nivel_ajuste] || "Ajuste medio"} · {ev.puntaje ?? 0}/100
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <a href={fileUrl(c.cv_file_id)} target="_blank" rel="noreferrer" className="px-2.5 py-1.5 text-xs rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1">
                                            <Eye size={12} /> Ver CV
                                        </a>
                                        <button onClick={() => removeCandidate(c.id)} className="px-2.5 py-1.5 text-xs rounded-lg border border-edu text-edu-muted hover:text-red-600 hover:border-red-300">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                                {ev.resumen && <p className="text-sm text-edu-body mb-2">{ev.resumen}</p>}
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    {ev.fortalezas && (
                                        <div>
                                            <p className="font-semibold text-edu-muted uppercase tracking-widest mb-1">Fortalezas</p>
                                            <p className="text-edu-body">{ev.fortalezas}</p>
                                        </div>
                                    )}
                                    {ev.brechas && (
                                        <div>
                                            <p className="font-semibold text-edu-muted uppercase tracking-widest mb-1">Brechas</p>
                                            <p className="text-edu-body">{ev.brechas}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
